import { useCallback } from 'react';

import { logger as LOG } from '@garnish/logger';

import type { HostedFrameMessageEvent } from '@sg/garnish';
import type { OrderType } from '@sg/graphql-schema';

import { useSaveFeedbackCase } from './useSaveFeedbackCase';

// ─────────────────────────────────────────────────────────────────────────────

export const useMomoSurveyMessages = (params: UseMomoSurveyMessagesParams) => {
  const { url, orderId, orderType } = params;
  const { saveFeedbackCase } = useSaveFeedbackCase();

  const onMessage = useCallback(
    async (event: HostedFrameMessageEvent) => {
      if (!isTrustedMomoOrigin(event, url)) return;

      const payload = parseMomoMessage(event.nativeEvent.data);

      if (!isSurveySubmitEvent(payload)) return;

      const result = await saveFeedbackCase({
        orderId,
        orderType,
        isFeedbackComplete: true,
      });

      if (result?.error) {
        logger.error('saveFeedbackCase failed', result.error);
      }
    },
    [orderId, orderType, saveFeedbackCase, url],
  );

  return { onMessage };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseMomoMessage(data: unknown): Record<string, unknown> | null {
  if (typeof data === 'object' && data !== null) {
    return data as Record<string, unknown>;
  }

  if (typeof data !== 'string' || !data) return null;

  try {
    const parsed = JSON.parse(data) as unknown;

    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function isSurveySubmitEvent(payload: Record<string, unknown> | null) {
  return payload?.eventName === 'Submit' && payload?.eventType === 'Click';
}

function isTrustedMomoOrigin(event: HostedFrameMessageEvent, fallbackUrl: string) {
  const messageUrl =
    'url' in event.nativeEvent && typeof event.nativeEvent.url === 'string'
      ? event.nativeEvent.url
      : fallbackUrl;

  try {
    return MOMOS_TRUSTED_ORIGINS.has(new URL(messageUrl).origin);
  } catch {
    return true;
  }
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MOMOS_TRUSTED_ORIGINS = new Set([
  'https://us-restaurant.momos.io',
  'https://us-web.momos.io',
]);

// ─── Logger ──────────────────────────────────────────────────────────────────

const logger = LOG.extend('MomoSurvey');

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMomoSurveyMessagesParams = Readonly<{
  url: string;
  orderId: string;
  orderType: OrderType;
}>;
